import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IDialogInboxClient,
  IParticipantDecision,
} from '../../Interfaces/IDialogInbox';
import { ILegalRoleTemplate } from '../../Interfaces/ILegalRoleTemplate';
import { ACTION_LABEL_SHORT, formatGermanDate } from '../i18n';

interface Props {
  participants: IDialogInboxClient[];
  decisions: Record<string, IParticipantDecision>;
  selectedTempId: string | null;
  onSelect: (tempId: string) => void;
  /** Setzt die getroffene Entscheidung (inkl. „Ignoriert") für den
   *  Beteiligten zurück — direkt aus der Liste heraus. */
  onResetDecision?: (tempId: string) => void;
  /** LegalRoleTemplates der gewählten Vorgangsart. Wenn leer/nicht gesetzt,
   *  ist die Rollenauswahl deaktiviert. */
  availableRoleTemplates: ILegalRoleTemplate[];
  rolesByTempId: Record<string, number | undefined>;
  onRoleChange: (tempId: string, idLegalRoleTemplate: number | null) => void;
  isTransactionTypeSelected: boolean;
}

const decisionChip = (
  decision?: IParticipantDecision
): { label: string; color: 'default' | 'success' | 'warning' | 'info' } => {
  if (!decision) return { label: 'Offen', color: 'default' };
  switch (decision.action) {
    case 'use':
      return { label: ACTION_LABEL_SHORT.use, color: 'success' };
    case 'merge':
      return { label: ACTION_LABEL_SHORT.merge, color: 'info' };
    case 'create':
      return { label: ACTION_LABEL_SHORT.create, color: 'success' };
    case 'ignore':
      return { label: ACTION_LABEL_SHORT.ignore, color: 'warning' };
  }
};

export const ParticipantsSection: React.FC<Props> = ({
  participants,
  decisions,
  selectedTempId,
  onSelect,
  onResetDecision,
  availableRoleTemplates,
  rolesByTempId,
  onRoleChange,
  isTransactionTypeSelected,
}) => {
  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        Beteiligte ({participants.length})
      </Typography>
      <List dense disablePadding>
        {participants.map(p => {
          const decision = decisions[p.tempId];
          const chip = decisionChip(decision);
          const name =
            p.idLegalClientType_hint === 'Juristische Person'
              ? p.LastName
              : [p.FirstName, p.LastName].filter(Boolean).join(' ');
          const isIgnored = decision?.action === 'ignore';
          const showRoleSelect = !isIgnored;
          const roleValue = rolesByTempId[p.tempId];
          // Rolle ist Pflicht, sobald die Vorgangsart Rollen anbietet. Solange
          // dann keine gewählt ist, wird das Feld als Fehler (rot) markiert.
          const roleSelectable =
            isTransactionTypeSelected && availableRoleTemplates.length > 0;
          const roleMissing = showRoleSelect && roleSelectable && !roleValue;

          return (
            <Box
              key={p.tempId}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'stretch',
                mb: 0.5,
              }}
            >
              <ListItemButton
                selected={selectedTempId === p.tempId}
                onClick={() => onSelect(p.tempId)}
                sx={{
                  borderRadius: 1,
                  flex: 1,
                  // Aktiven Beteiligten klar hervorheben — damit eindeutig
                  // ist, auf wen sich die Abgleichsfläche darunter bezieht.
                  '&.Mui-selected': {
                    bgcolor: 'action.selected',
                    boxShadow: theme =>
                      `inset 4px 0 0 ${theme.palette.primary.main}`,
                  },
                  '&.Mui-selected:hover': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {name || '(ohne Namen)'}
                      </Typography>
                      <Chip size='small' label={p.role} variant='outlined' />
                      <Tooltip
                        title={
                          decision && onResetDecision
                            ? 'Markierung zurücksetzen'
                            : ''
                        }
                      >
                        <Chip
                          size='small'
                          label={chip.label}
                          color={chip.color}
                          onDelete={
                            decision && onResetDecision
                              ? (e: React.MouseEvent) => {
                                  // Klick aufs X nicht zur ListItemButton
                                  // durchschlagen lassen (sonst Re-Select).
                                  e.stopPropagation();
                                  onResetDecision(p.tempId);
                                }
                              : undefined
                          }
                        />
                      </Tooltip>
                    </Box>
                  }
                  secondary={
                    <Typography variant='caption' color='text.secondary'>
                      {[
                        p.Email,
                        p.Birthdate
                          ? `geb. ${formatGermanDate(p.Birthdate)}`
                          : null,
                        p.idBusinessForm_hint,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  }
                />
              </ListItemButton>
              {showRoleSelect && (
                <Stack
                  justifyContent='center'
                  sx={{ minWidth: 220, flexShrink: 0 }}
                  // Klicks und Mausevents auf dem Select-Container nicht zur
                  // umgebenden ListItemButton durchschlagen lassen — sonst
                  // schließt MUI das Menu sofort wieder, weil der Klick als
                  // "Klick außerhalb" gewertet wird.
                  onClick={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                >
                  <FormControl
                    size='small'
                    fullWidth
                    required={roleSelectable}
                    error={roleMissing}
                  >
                    <InputLabel>Rolle im Vorgang</InputLabel>
                    <Select
                      label='Rolle im Vorgang'
                      value={roleValue ?? ''}
                      disabled={
                        !isTransactionTypeSelected ||
                        availableRoleTemplates.length === 0
                      }
                      onChange={e => {
                        const v = e.target.value;
                        onRoleChange(p.tempId, v === '' ? null : Number(v));
                      }}
                    >
                      <MenuItem value=''>
                        <em>(keine)</em>
                      </MenuItem>
                      {availableRoleTemplates.map(rt => (
                        <MenuItem
                          key={rt.idLegalRoleTemplate}
                          value={rt.idLegalRoleTemplate}
                        >
                          {rt.LegalRoleTemplate}
                        </MenuItem>
                      ))}
                    </Select>
                    {roleMissing && (
                      <FormHelperText>
                        Pflichtfeld: bitte Rolle auswählen
                      </FormHelperText>
                    )}
                  </FormControl>
                  {!isTransactionTypeSelected ? (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ mt: 0.5 }}
                    >
                      Bitte zuerst die Vorgangsart oben wählen
                    </Typography>
                  ) : availableRoleTemplates.length === 0 ? (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ mt: 0.5 }}
                    >
                      Für diese Vorgangsart sind keine Rollen hinterlegt
                    </Typography>
                  ) : null}
                </Stack>
              )}
            </Box>
          );
        })}
      </List>
    </Paper>
  );
};
